import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Download, Building2, Phone, Globe } from 'lucide-react';
import { Button, Input, Card, Pagination, SortableHeader, AdvancedSearch, type SortDirection } from '../components/ui';
import { API_BASE_URL, getAuthToken } from '../lib/api';

interface Account {
  id: string;
  accountId: string;
  accountName: string;
  tradeStatus: string;
  prefecture: string;
  city: string;
  telMain: string;
  website: string;
  updatedAt: string;
}

const TRADE_STATUS_OPTIONS = [
  { value: '取引中', label: '取引中' },
  { value: '休眠', label: '休眠' },
  { value: '見込み', label: '見込み' },
  { value: '失注', label: '失注' },
];

const PREFECTURE_OPTIONS = [
  '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県',
  '岐阜県', '静岡県', '愛知県', '三重県',
  '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
  '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県',
  '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県',
].map(p => ({ value: p, label: p }));

const SEARCH_FIELDS = [
  { key: 'accountId', label: '企業ID', type: 'text' as const, placeholder: 'ACC-001' },
  { key: 'accountName', label: '企業名', type: 'text' as const, placeholder: '株式会社...' },
  { key: 'tradeStatus', label: '取引状況', type: 'select' as const, options: TRADE_STATUS_OPTIONS },
  { key: 'prefecture', label: '都道府県', type: 'select' as const, options: PREFECTURE_OPTIONS },
  { key: 'city', label: '市区町村', type: 'text' as const, placeholder: '渋谷区' },
  { key: 'updatedAt', label: '更新日', type: 'daterange' as const },
];

export function Accounts() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // 検索条件
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  
  // ページネーション
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(30);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // ソート
  const [sortKey, setSortKey] = useState<string | null>('updatedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    fetchAccounts();
  }, [page, limit, sortKey, sortDirection]);

  const fetchAccounts = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...(sortKey && sortDirection && { sortBy: sortKey, sortOrder: sortDirection }),
        ...Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v !== '')
        ),
      });
      
      const headers: Record<string, string> = {};
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/accounts?${params}`, {
        credentials: 'include',
        headers,
      });
      const data = await response.json();
      
      setAccounts(data.data || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      console.error('Fetch accounts error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchAccounts();
  };

  const handleReset = () => {
    setSearch('');
    setFilters({});
    setPage(1);
    fetchAccounts();
  };

  const handleSort = (key: string, direction: SortDirection) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction);
    setPage(1);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      '取引中': 'bg-green-100 text-green-700',
      '休眠': 'bg-gray-100 text-gray-700',
      '見込み': 'bg-blue-100 text-blue-700',
      '失注': 'bg-red-100 text-red-700',
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            企業管理
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            {total.toLocaleString()} 件の企業
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" leftIcon={<Download size={16} />}>
            CSV出力
          </Button>
          <Button leftIcon={<Plus size={16} />} onClick={() => navigate('/accounts/new')}>
            新規作成
          </Button>
        </div>
      </div>

      {/* 検索・フィルター */}
      <Card padding="sm">
        <AdvancedSearch
          fields={SEARCH_FIELDS}
          values={filters}
          onChange={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))}
          onSearch={handleSearch}
          onReset={handleReset}
        >
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="企業名・企業IDで検索..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search size={18} />}
            />
          </div>
          <Button type="button" onClick={handleSearch}>
            検索
          </Button>
        </AdvancedSearch>
      </Card>

      {/* テーブル */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <p style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
          </div>
        ) : accounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <Building2 size={48} className="text-gray-300 mb-4" />
            <p style={{ color: 'var(--text-secondary)' }}>企業がありません</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <SortableHeader
                    label="企業名"
                    sortKey="accountName"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="取引状況"
                    sortKey="tradeStatus"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="所在地"
                    sortKey="prefecture"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <th className="text-left px-4 py-3 text-sm font-medium" style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--light-gray-primary)' }}>
                    連絡先
                  </th>
                  <SortableHeader
                    label="更新日"
                    sortKey="updatedAt"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                {accounts.map((account) => (
                  <tr
                    key={account.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/accounts/${account.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                          {account.accountName}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          {account.accountId}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(account.tradeStatus)}`}>
                        {account.tradeStatus || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>
                      {account.prefecture || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {account.telMain && (
                          <span className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                            <Phone size={14} />
                            {account.telMain}
                          </span>
                        )}
                        {account.website && (
                          <a 
                            href={account.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Globe size={14} />
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {new Date(account.updatedAt).toLocaleDateString('ja-JP')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ページネーション */}
        {!isLoading && total > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={handleLimitChange}
          />
        )}
      </Card>
    </div>
  );
}
